import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { ArrangementResource } from '@/lib/types';

export function useArrangementResources(arrangementId: string): ReturnType<typeof useQuery<ArrangementResource[]>> {
  return useQuery({
    queryKey: ['arrangements', arrangementId, 'resources'],
    queryFn: () => api.fetch<ArrangementResource[]>(`/arrangements/${arrangementId}/resources`),
    enabled: Boolean(arrangementId),
  });
}

type AddLinkRequest = {
  arrangementId: string;
  resourceType: string;
  sourceType: string;
  title: string;
  url: string;
};

export function useAddResourceLink(): ReturnType<typeof useMutation<ArrangementResource, Error, AddLinkRequest>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arrangementId, ...data }: AddLinkRequest) =>
      api.fetch<ArrangementResource>(`/arrangements/${arrangementId}/resources`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['arrangements', variables.arrangementId, 'resources'] });
    },
  });
}

type UploadFileRequest = {
  arrangementId: string;
  file: File;
  resourceType: string;
  title: string;
};

export function useUploadResourceFile(): ReturnType<typeof useMutation<ArrangementResource, Error, UploadFileRequest>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ arrangementId, file, resourceType, title }: UploadFileRequest) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('resourceType', resourceType);
      formData.append('title', title);
      const url = `/api/arrangements/${arrangementId}/resources/upload`;
      const token = api.getToken();
      const res = await fetch(url, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      return res.json() as Promise<ArrangementResource>;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['arrangements', variables.arrangementId, 'resources'] });
    },
  });
}

export function useDeleteResource(): ReturnType<typeof useMutation<void, Error, { arrangementId: string; resourceId: string }>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arrangementId, resourceId }) =>
      api.fetch<void>(`/arrangements/${arrangementId}/resources/${resourceId}`, { method: 'DELETE' }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['arrangements', variables.arrangementId, 'resources'] });
    },
  });
}
